import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ImagePlus, SwitchCamera } from 'lucide-react';
import Dialog from './ui/Dialog';

type FacingMode = 'environment' | 'user';

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onCancel?: () => void;
  /** Button layout: grid cell, full-width, or chat attach menu row */
  variant?: 'grid' | 'full' | 'menuItem';
  className?: string;
}

export default function CameraCapture({
  onCapture,
  onCancel,
  variant = 'grid',
  className = '',
}: CameraCaptureProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [facing, setFacing] = useState<FacingMode>('environment');
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fallbackRef = useRef<HTMLInputElement>(null);
  /** The file picker inside the dialog (the app shell is inert while it is open). */
  const dialogFileRef = useRef<HTMLInputElement>(null);

  /** The viewfinder mounts with the dialog; attach a stream that arrived first. */
  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    const stream = streamRef.current;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      el.play().then(() => setReady(true)).catch(() => {});
    }
  }, []);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
  }, []);

  const close = useCallback(() => {
    stopStream();
    setOpen(false);
    setError(null);
    onCancel?.();
  }, [stopStream, onCancel]);

  const startStream = useCallback(async (mode: FacingMode) => {
    stopStream();
    setError(null);
    setReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(t('tickets.cameraUnavailable'));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: mode },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setReady(true);
      }
    } catch {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setReady(true);
        }
      } catch {
        // The picker needs a user gesture: offer it as a button in the dialog.
        setError(t('tickets.cameraUnavailable'));
      }
    }
  }, [stopStream, t]);

  const openCamera = useCallback(() => {
    // No camera API (old browser / insecure origin): the device's own picker, straight away.
    if (!navigator.mediaDevices?.getUserMedia) {
      fallbackRef.current?.click();
      return;
    }
    setOpen(true);
    void startStream(facing);
  }, [facing, startStream]);

  const flipCamera = useCallback(() => {
    const next: FacingMode = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    void startStream(next);
  }, [facing, startStream]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !ready) return;
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
        close();
      },
      'image/jpeg',
      0.92,
    );
  }, [ready, onCapture, close]);

  const onFallbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (f) {
      onCapture(f);
      close();
    }
  };

  useEffect(() => {
    if (!open) return;
    return () => stopStream();
  }, [open, stopStream]);

  const triggerClass =
    variant === 'menuItem'
      ? 'flex items-center gap-3 w-full px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors'
      : variant === 'grid'
        ? 'flex flex-col items-center gap-1.5 py-3 px-2 rounded-lg border-2 border-dashed border-surface-300/60 hover:border-primary-400 hover:bg-primary-50 transition-colors min-h-touch'
        : 'flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border-2 border-dashed border-primary-300 bg-primary-50 hover:bg-accent-soft text-primary-800 min-h-14';

  const label = variant === 'menuItem' ? t('chat.camera') : t('tickets.takePhoto');

  return (
    <>
      <button type="button" onClick={openCamera} className={`${triggerClass} ${className}`}>
        {variant === 'menuItem' ? (
          <>
            <Camera size={18} className="text-surface-500" aria-hidden="true" />
            <span>{label}</span>
          </>
        ) : (
          <>
            <Camera size={variant === 'grid' ? 18 : 20} strokeWidth={1.75} className={variant === 'grid' ? undefined : 'text-primary-700'} aria-hidden="true" />
            <span className={variant === 'grid' ? 'text-xs text-surface-600' : 'text-sm'}>{label}</span>
          </>
        )}
      </button>

      <input
        ref={fallbackRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFallbackChange}
      />

      <Dialog
        open={open}
        onClose={close}
        title={t('cameraCapture.title')}
        size="xl"
        detent="large"
        bodyClassName="p-0"
        footer={(
          <>
            <button
              type="button"
              onClick={flipCamera}
              disabled={!ready}
              className="btn btn-ghost me-auto"
            >
              <SwitchCamera size={16} aria-hidden="true" />
              {t('cameraCapture.switch')}
            </button>
            <button type="button" onClick={close} className="btn btn-secondary">{t('common.cancel')}</button>
            <button type="button" onClick={capturePhoto} disabled={!ready} className="btn btn-primary">
              <Camera size={16} aria-hidden="true" />
              {t('cameraCapture.capture')}
            </button>
          </>
        )}
      >
        <div className="relative flex min-h-[50dvh] items-center justify-center overflow-hidden bg-scrim">
          <video
            ref={attachVideo}
            playsInline
            muted
            autoPlay
            className="max-h-[70dvh] max-w-full object-contain"
            aria-label={t('cameraCapture.viewfinder')}
          />
          {!ready && !error && (
            <p className="absolute inset-x-0 text-center text-sm text-surface-0/80" role="status">{t('cameraCapture.starting')}</p>
          )}
          {error && !ready && (
            <div className="absolute inset-x-0 flex flex-col items-center gap-3 px-6 text-center" role="status">
              <p className="text-sm text-surface-0/90">{error}</p>
              <button type="button" onClick={() => dialogFileRef.current?.click()} className="btn btn-secondary">
                <ImagePlus size={16} aria-hidden="true" />
                {t('cameraCapture.pickFile')}
              </button>
              <input
                ref={dialogFileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={onFallbackChange}
              />
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
